import React, { createContext, useState, useEffect, ReactNode } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../config/firebase';
import { CognitoUser, MemberProfile, AuthState, ChapterBranding } from '../types/auth';
import { authService } from '../services/authService';
import { presenceService } from '../services/presenceService';
import { belongsToCurrentOrg } from '../utils/orgScope';
import { isPlatformAdminHost, tenantSupportsProfileClaim } from '../config/tenant';
import { ensureFirebaseSignIn, firebaseSignOut } from '../services/firebaseAuthBridge';

interface AuthContextType extends AuthState {
  signIn: (email: string, password: string) => Promise<void>;
  signUp: (
    email: string,
    password: string,
    attributes: { given_name: string; family_name: string },
  ) => Promise<void>;
  signOut: () => Promise<void>;
  refreshSession: () => Promise<void>;
  refreshProfile: () => Promise<void>;
  updateProfile: (profile: MemberProfile) => void;
  requiresProfileClaim: boolean;
  skipProfileClaim: () => void;
  hasSkippedProfileClaim: boolean;
}

export const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();

  const [state, setState] = useState<AuthState>({
    user: null,
    profile: null,
    primaryChapter: null,
    loading: true,
    error: null,
  });

  const [hasSkippedProfileClaim, setHasSkippedProfileClaim] = useState(false);

  // Load branding for the user's primary chapter
  const loadPrimaryChapterBranding = async (profile: MemberProfile): Promise<ChapterBranding | null> => {
    const primaryMembership = profile.entityMemberships?.[0];
    if (!primaryMembership?.entityId) return null;
    try {
      const entityDoc = await getDoc(doc(db, 'entities', primaryMembership.entityId));
      if (!entityDoc.exists()) return null;
      const data = entityDoc.data();
      if (!belongsToCurrentOrg(data)) return null;
      return {
        entityId: entityDoc.id,
        name: data.name || primaryMembership.entityName || '',
        logoUrl: data.logoUrl || undefined,
        brandName: data.brandName || undefined,
        imageUrl: data.imageUrl || undefined,
      };
    } catch (err) {
      console.warn('[AuthContext] Failed to load chapter branding:', err);
      return null;
    }
  };

  // Determine if profile claiming is required. Only tenants that use the
  // claim-a-pre-existing-profile flow (Konklave) do this; CrimsonConnect /
  // the Konklave platform register a fresh member at sign-up, so they never claim.
  const requiresProfileClaim = Boolean(
    state.user && !state.profile && !state.loading && tenantSupportsProfileClaim(),
  );

  // Initialize auth state on mount
  useEffect(() => {
    initializeAuth();
  }, []);

  // Handle automatic redirection to profile claiming
  useEffect(() => {
    // Don't redirect if we're already on the claim-profile page or if user has skipped
    if (
      requiresProfileClaim &&
      !hasSkippedProfileClaim &&
      !isPlatformAdminHost() && // Platform super-admins manage tenants, not member profiles
      location.pathname !== '/claim-profile' &&
      !location.pathname.startsWith('/events/public/') // Don't redirect on public event pages
    ) {
      console.log('[AuthContext] Profile claiming required, redirecting to /claim-profile');
      navigate('/claim-profile', { replace: true });
    }
  }, [requiresProfileClaim, hasSkippedProfileClaim, location.pathname, navigate]);

  // Reset skip state when profile is successfully linked
  useEffect(() => {
    if (state.profile) {
      setHasSkippedProfileClaim(false);
    }
  }, [state.profile]);

  // Start/stop presence tracking based on auth state
  useEffect(() => {
    if (state.user && state.profile) {
      presenceService.start();
    } else {
      presenceService.stop();
    }
    return () => presenceService.stop();
  }, [state.user, state.profile]);

  // Set up token refresh interval
  useEffect(() => {
    if (state.user) {
      // Refresh token every 50 minutes (tokens expire after 60 minutes)
      const refreshInterval = setInterval(
        () => {
          authService.refreshSession().catch(error => {
            console.error('Token refresh failed:', error);
          });
        },
        50 * 60 * 1000,
      );

      return () => clearInterval(refreshInterval);
    }
  }, [state.user]);

  const initializeAuth = async () => {
    try {
      console.log('[AuthContext] Initializing auth...');
      const user = await authService.getCurrentUser();
      console.log('[AuthContext] Current user:', user ? user.email : 'none');

      if (user) {
        // Establish the Firebase Auth session (bridged from Cognito) before any
        // direct Firestore reads so firestore.rules can authorize them.
        await ensureFirebaseSignIn().catch(err =>
          console.warn('[AuthContext] Firebase sign-in bridge failed:', err),
        );
        console.log('[AuthContext] Fetching member profile for userId:', user.userId);
        let profile = await authService.getMemberProfile(user.userId);

        // If no profile found, auto-register (handles legacy users)
        if (!profile) {
          console.log('[AuthContext] No profile found, auto-registering...');
          try {
            await authService.register({
              firstName: user.given_name || '',
              lastName: user.family_name || '',
              email: user.email || '',
            });
            // Retry loading profile
            profile = await authService.getMemberProfile(user.userId);
          } catch (regErr) {
            console.warn('[AuthContext] Auto-registration failed:', regErr);
          }
        }

        const primaryChapter = profile ? await loadPrimaryChapterBranding(profile) : null;

        setState({
          user,
          profile,
          primaryChapter,
          loading: false,
          error: null,
        });
      } else {
        setState({
          user: null,
          profile: null,
          primaryChapter: null,
          loading: false,
          error: null,
        });
      }
    } catch (error) {
      console.error('[AuthContext] Auth initialization error:', error);
      setState({
        user: null,
        profile: null,
        primaryChapter: null,
        loading: false,
        error: error instanceof Error ? error.message : 'Authentication failed',
      });
    }
  };

  const signIn = async (email: string, password: string) => {
    try {
      setState(prev => ({ ...prev, error: null }));

      const user = await authService.signIn(email, password);
      // Bridge the Cognito session into Firebase Auth before direct Firestore reads.
      await ensureFirebaseSignIn().catch(err =>
        console.warn('[AuthContext] Firebase sign-in bridge failed:', err),
      );
      const profile = await authService.getMemberProfile(user.userId);
      const primaryChapter = profile ? await loadPrimaryChapterBranding(profile) : null;

      setState({
        user,
        profile,
        primaryChapter,
        loading: false,
        error: null,
      });

      // If no profile is found, the useEffect will handle redirection to claim-profile
      if (!profile) {
        console.log(
          '[AuthContext] No profile found after sign in, profile claiming will be required',
        );
      }

      // Fire-and-forget: sync profile from Salesforce in the background
      if (profile) {
        import('../config/api').then(({ apiRequest }) => {
          apiRequest('/auth/sync-profile', { method: 'POST' })
            .then((result: any) => {
              if (result?.synced && result?.updated) {
                console.log('[AuthContext] Profile synced from Salesforce:', result.fieldsUpdated);
              }
            })
            .catch((err: any) => {
              console.warn('[AuthContext] Background profile sync failed:', err.message);
            });
        });
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Sign in failed';
      setState({
        user: null,
        profile: null,
        primaryChapter: null,
        loading: false,
        error: errorMessage,
      });
      throw error;
    }
  };

  const signUp = async (
    email: string,
    password: string,
    attributes: { given_name: string; family_name: string },
  ) => {
    try {
      setState(prev => ({ ...prev, loading: true, error: null }));

      await authService.signUp(email, password, attributes);

      setState(prev => ({
        ...prev,
        loading: false,
        error: null,
      }));
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Sign up failed';
      setState(prev => ({
        ...prev,
        loading: false,
        error: errorMessage,
      }));
      throw error;
    }
  };

  const signOut = async () => {
    try {
      presenceService.stop();
      await firebaseSignOut();
      await authService.signOut();
      setState({
        user: null,
        profile: null,
        primaryChapter: null,
        loading: false,
        error: null,
      });
    } catch (error) {
      console.error('Sign out error:', error);
      throw error;
    }
  };

  const refreshSession = async () => {
    try {
      await authService.refreshSession();
    } catch (error) {
      console.error('Session refresh error:', error);
      // If refresh fails, sign out the user
      await signOut();
      throw error;
    }
  };

  // Re-fetch the member profile from the backend and push it into state. Used
  // after sign-up/invite-redemption, where the member doc (and its now-verified
  // status) is created *after* the initial signIn read returned null — without
  // this, the app would keep a stale null profile (wrongly prompting a profile
  // claim and never showing the member as verified).
  const refreshProfile = async () => {
    const currentUser = state.user || (await authService.getCurrentUser());
    if (!currentUser) return;
    await ensureFirebaseSignIn().catch(err =>
      console.warn('[AuthContext] Firebase sign-in bridge failed:', err),
    );
    const profile = await authService.getMemberProfile(currentUser.userId);
    const primaryChapter = profile ? await loadPrimaryChapterBranding(profile) : null;
    setState(prev => ({
      ...prev,
      user: currentUser,
      profile,
      primaryChapter,
      loading: false,
    }));
  };

  const updateProfile = (profile: MemberProfile) => {
    setState(prev => ({
      ...prev,
      profile,
    }));
  };

  const skipProfileClaim = () => {
    console.log('[AuthContext] User skipped profile claiming');
    setHasSkippedProfileClaim(true);
  };

  const value: AuthContextType = {
    ...state,
    signIn,
    signUp,
    signOut,
    refreshSession,
    refreshProfile,
    updateProfile,
    requiresProfileClaim,
    skipProfileClaim,
    hasSkippedProfileClaim,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
